import { z } from 'zod';

/**
 * Sprint 161 — **Qué pantallas se usan, qué se toca y qué se deja a medias.**
 *
 * El análisis del 18/10/2026 dejó preguntas que la base del negocio no contesta: ¿la dueña no usa el
 * enlace del menú porque no lo encuentra o porque le da miedo?, ¿empieza ventas y las abandona?, ¿qué
 * pantallas nunca abre? Esto registra **hábitos**, no datos: el nombre de la pantalla, el nombre de
 * un botón marcado a propósito (`data-uso`) y cuánto tiempo se estuvo. **Nunca lo que se escribe.**
 *
 * La regla de privacidad vive en el esquema, no en la buena voluntad de quien llame: la pantalla es
 * una ruta (los números se reemplazan) y el nombre es un identificador en minúsculas sin espacios,
 * así que un nombre de cliente, un teléfono o un monto **no caben** aunque alguien intente mandarlos.
 */

export const TIPOS_EVENTO_USO = ['pantalla', 'toque', 'flujo'] as const;

/** Ruta normalizada: `/pedidos/123?x=1` → `/pedidos/:n`. Nunca lleva datos del negocio. */
export function normalizarPantalla(ruta: string): string {
  const sinQuery = ruta.split(/[?#]/)[0] || '/';
  const limpia = sinQuery
    .toLowerCase()
    .split('/')
    .map((seg, i, todos) => {
      // Auditoría `cyber` S161: la página del pedido del cliente lleva su llave en la ruta; nunca se guarda.
      if (i > 0 && todos[i - 1] === 'pedido' && seg) return ':token';
      if (/^\d+$/.test(seg)) return ':n';
      // Cualquier tramo largo con números es un identificador (token, folio), no un nombre de pantalla.
      if (seg.length >= 16 && /\d/.test(seg)) return ':x';
      return seg;
    })
    .join('/')
    .replace(/[^a-z0-9\-/:]/g, '')
    .slice(0, 80);
  return limpia.startsWith('/') ? limpia : `/${limpia}`;
}

/**
 * Un nombre es `familia:accion[:...]` y empieza con letra: `venta:confirmar`, `caja:ahora-no`,
 * `venta:inicio:3fa2b1c9`. Un teléfono, un monto o un nombre propio no tienen esa forma.
 */
export const NOMBRE_USO = /^[a-z][a-z-]{1,20}:[a-z][a-z-]{0,30}$/;
/** Un flujo: `familia:inicio|fin:<8 hex>`. Auditoría `cyber` S161: sin dígitos sueltos donde quepa un teléfono. */
export const NOMBRE_FLUJO = /^[a-z][a-z-]{1,20}:(inicio|fin):[0-9a-f]{8}$/;

export const EventoUsoSchema = z.object({
  tipo: z.enum(TIPOS_EVENTO_USO),
  pantalla: z
    .string()
    .max(80)
    .regex(/^\/[a-z0-9\-/:]*$/, 'pantalla inválida')
    .refine((p) => p === normalizarPantalla(p), 'pantalla sin normalizar'),
  nombre: z.string().max(60).optional(),
  duracionMs: z.number().int().min(0).max(86_400_000).optional(),
  /** Hora del navegador (ms). El servidor la acota a ±1 día de la suya. */
  en: z.number().int(),
}).refine(
  (e) => e.nombre === undefined || (e.tipo === 'flujo' ? NOMBRE_FLUJO : NOMBRE_USO).test(e.nombre),
  'nombre inválido',
);
export type EventoUso = z.infer<typeof EventoUsoSchema>;

export const LoteUsoSchema = z.object({
  visita: z.string().uuid(),
  eventos: z.array(EventoUsoSchema).min(1).max(100),
});

/**
 * Auditoría `cyber` S161 — Un evento malo no tira el lote: se valida la envoltura y cada evento por
 * separado, y se guardan sólo los buenos.
 */
export function leerLoteUso(cuerpo: unknown): { visita: string; eventos: EventoUso[] } | null {
  const env = z.object({ visita: z.string().uuid(), eventos: z.array(z.unknown()).min(1).max(100) }).safeParse(cuerpo);
  if (!env.success) return null;
  const eventos = env.data.eventos.flatMap((e) => {
    const r = EventoUsoSchema.safeParse(e);
    return r.success ? [r.data] : [];
  });
  return eventos.length ? { visita: env.data.visita, eventos } : null;
}
export type LoteUso = z.infer<typeof LoteUsoSchema>;

// ---------------------------------------------------------------------------------------------
// El censo: de eventos crudos a respuestas que José pueda leer.
// ---------------------------------------------------------------------------------------------

export interface EventoGuardado {
  fecha: string | Date;
  rol: string;
  visita: string;
  tipo: string;
  pantalla: string;
  nombre: string | null;
  duracionMs: number | null;
}

export interface CensoUso {
  visitas: number;
  dias: number;
  porPantalla: Array<{ pantalla: string; veces: number; minutos: number }>;
  toquesMarcados: Array<{ nombre: string; veces: number }>;
  toquesPorPantalla: Array<{ pantalla: string; toques: number }>;
  flujos: Array<{ flujo: string; empezados: number; terminados: number; abandonados: number }>;
  nuncaVistas: string[];
}

/**
 * Resume los eventos. `pantallasConocidas` es el menú de la app: lo que no aparece en los eventos
 * es lo que nadie abrió en el periodo.
 */
export function censar(eventos: EventoGuardado[], pantallasConocidas: string[] = []): CensoUso {
  const dias = new Set(eventos.map((e) => new Date(e.fecha).toISOString().slice(0, 10)));
  const visitas = new Set(eventos.map((e) => e.visita));

  const pant = new Map<string, { veces: number; ms: number }>();
  const marcados = new Map<string, number>();
  const toques = new Map<string, number>();
  const flujos = new Map<string, { inicio: Set<string>; fin: Set<string> }>();

  for (const e of eventos) {
    if (e.tipo === 'pantalla') {
      const p = pant.get(e.pantalla) ?? { veces: 0, ms: 0 };
      p.veces += 1;
      p.ms += e.duracionMs ?? 0;
      pant.set(e.pantalla, p);
    } else if (e.tipo === 'toque') {
      toques.set(e.pantalla, (toques.get(e.pantalla) ?? 0) + 1);
      if (e.nombre) marcados.set(e.nombre, (marcados.get(e.nombre) ?? 0) + 1);
    } else if (e.tipo === 'flujo' && e.nombre) {
      // nombre = "<flujo>:<inicio|fin>:<clave>" — la clave une el inicio con su fin.
      const [flujo, etapa, clave = e.visita] = e.nombre.split(':');
      const f = flujos.get(flujo) ?? { inicio: new Set<string>(), fin: new Set<string>() };
      if (etapa === 'inicio') f.inicio.add(clave);
      if (etapa === 'fin') f.fin.add(clave);
      flujos.set(flujo, f);
    }
  }

  const vistas = new Set(pant.keys());
  return {
    visitas: visitas.size,
    dias: dias.size,
    porPantalla: [...pant.entries()]
      .map(([pantalla, v]) => ({ pantalla, veces: v.veces, minutos: Math.round(v.ms / 60_000) }))
      .sort((a, b) => b.minutos - a.minutos || b.veces - a.veces),
    toquesMarcados: [...marcados.entries()].map(([nombre, veces]) => ({ nombre, veces })).sort((a, b) => b.veces - a.veces),
    toquesPorPantalla: [...toques.entries()].map(([pantalla, t]) => ({ pantalla, toques: t })).sort((a, b) => b.toques - a.toques),
    flujos: [...flujos.entries()].map(([flujo, f]) => {
      const terminados = [...f.inicio].filter((c) => f.fin.has(c)).length;
      return { flujo, empezados: f.inicio.size, terminados, abandonados: f.inicio.size - terminados };
    }),
    nuncaVistas: pantallasConocidas.filter((p) => !vistas.has(p)),
  };
}
